import { Checkbox, GlobalToken, Select, Switch, theme } from "antd";
import { Flex } from "src/components/Flex";
import { errorColors, successColors } from "src/utils/color";

export enum ChangeColor {
  TEXT = "text",
  GOOD = "success",
  BAD = "error",
  SWAP1 = "green-red",
  SWAP2 = "red-green",
}

export function isChangeColor(color: ChangeColor | string) {
  return Object.values(ChangeColor).includes(color as ChangeColor);
}

export type CompareWithConfig = {
  showChangeAs: "value" | "percentage";
  showChangeValues: boolean;
  changeColor: ChangeColor;
};

export const toCompareWithConfig: (
  data: CompareWithConfig | any,
  defaults?: Partial<CompareWithConfig>
) => CompareWithConfig = (data, defaults = {}) => ({
  showChangeAs: ["value", "percentage"].includes(data?.showChangeAs)
    ? data.showChangeAs
    : defaults.showChangeAs || "percentage",
  showChangeValues:
    typeof data?.showChangeValues === "boolean"
      ? data.showChangeValues
      : defaults.showChangeValues === undefined
      ? true
      : defaults.showChangeValues,
  changeColor:
    typeof data?.changeColor === "string" &&
    Object.values(ChangeColor).includes(data.changeColor)
      ? data.changeColor
      : defaults.changeColor || "text",
});

export function CompareWithControls(
  props: {
    disabled: boolean;
    onChangeShowChangeAs?: (as: string) => void;
    onChangeShowChangeValues?: (visible: boolean) => void;
    onChangeColor?: (changeColor: string) => void;
  } & Partial<CompareWithConfig>
) {
  const { token } = theme.useToken();
  const positiveColor = successColors()[1];
  const negativeColor = errorColors()[1];

  return props.disabled ? null : (
    <Flex direction="column" style={{ width: "100%" }}>
      {props.onChangeColor && (
        <Flex spaceBetween style={{ width: "100%", marginTop: 8 }}>
          <div className="viz-label" style={{ alignSelf: "flex-start" }}>
            Change color
          </div>
          <Select
            style={{ alignSelf: "flex-end" }}
            className={["viz-select", props.disabled ? "disabled" : ""].join(
              " "
            )}
            disabled={props.disabled}
            value={props.changeColor}
            onChange={(color) => props.onChangeColor!(color)}
            popupMatchSelectWidth={false}
            bordered={false}
            options={[
              {
                value: ChangeColor.TEXT,
                label: (
                  <span className="viz-select-option">
                    <Swatch color={token.colorText} />
                    Neutral
                  </span>
                ),
              },
              {
                value: ChangeColor.GOOD,
                label: (
                  <span className="viz-select-option">
                    <Swatch color={positiveColor} />
                    Success
                  </span>
                ),
              },
              {
                value: ChangeColor.BAD,
                label: (
                  <span className="viz-select-option">
                    <Swatch color={negativeColor} />
                    Warning
                  </span>
                ),
              },
              {
                value: ChangeColor.SWAP1,
                label: (
                  <span className="viz-select-option">
                    <DoubleSwatch
                      color1={positiveColor}
                      color2={negativeColor}
                    />
                    Success/Warn
                  </span>
                ),
              },
              {
                value: ChangeColor.SWAP2,
                label: (
                  <span className="viz-select-option">
                    <DoubleSwatch
                      color1={negativeColor}
                      color2={positiveColor}
                    />
                    Warn/Success
                  </span>
                ),
              },
            ]}
          />
        </Flex>
      )}
      <Flex gap={4} direction="column" style={{ width: "100%", marginTop: 8 }}>
        {props.onChangeShowChangeAs && (
          <Flex style={{ width: "100%" }} spaceBetween>
            <span
              className={["viz-label", props.disabled ? "disabled" : ""].join(
                " "
              )}
              style={{ alignSelf: "flex-start" }}
            >
              Show change as
            </span>
            <Select
              style={{ alignSelf: "flex-end" }}
              className={["viz-select", props.disabled ? "disabled" : ""].join(
                " "
              )}
              disabled={props.disabled}
              value={props.showChangeAs}
              onChange={(changeAs) => props.onChangeShowChangeAs!(changeAs)}
              popupMatchSelectWidth={false}
              bordered={false}
              options={[
                {
                  value: "value",
                  label: <span className="viz-select-option">Value delta</span>,
                },
                {
                  value: "percentage",
                  label: <span className="viz-select-option">Percent</span>,
                },
              ]}
            />
          </Flex>
        )}
        {props.onChangeShowChangeValues && (
          <label style={{ cursor: "pointer", width: "100%", marginTop: 10 }}>
            <Flex spaceBetween style={{ width: "100%", paddingRight: 2 }}>
              <span
                className={["viz-label", props.disabled ? "disabled" : ""].join(
                  " "
                )}
                style={{ alignSelf: "flex-start" }}
              >
                Show value
              </span>
              <Switch
                style={{ alignSelf: "flex-end" }}
                checked={props.showChangeValues}
                disabled={props.disabled}
                onChange={(checked) => props.onChangeShowChangeValues!(checked)}
                size="small"
              />
            </Flex>
          </label>
        )}
      </Flex>
    </Flex>
  );
}

function DoubleSwatch({ color1, color2 }: { color1: string; color2: string }) {
  const { token } = theme.useToken();
  return (
    <div
      style={{
        marginRight: 4,
        borderRadius: 3,
        height: 16,
        width: 16,
        display: "inline-block",
        border: `solid 1px ${token.colorTextLightSolid}`,
        backgroundImage: `linear-gradient(135deg, ${color1} 50%, ${color2} 50%)`,
      }}
    />
  );
}

function Swatch({ color }: { color: string }) {
  const { token } = theme.useToken();
  return (
    <div
      style={{
        marginRight: 4,
        borderRadius: 3,
        height: 16,
        width: 16,
        display: "inline-block",
        border: `solid 1px ${token.colorTextLightSolid}`,
        backgroundColor: color,
      }}
    />
  );
}

export function useChangeColor(color: ChangeColor, label: string | number) {
  const { token } = theme.useToken();
  return getChangeColor(token, color, label);
}

export function getChangeColor(
  token: GlobalToken,
  color: ChangeColor,
  value: string | number
) {
  if (
    [ChangeColor.SWAP1, ChangeColor.SWAP2].includes(color) &&
    getChangeArrowAngle(value) === 0
  ) {
    return token.colorTextSecondary;
  }

  const label = `${value}`;
  const positiveColor = successColors()[1];
  const negativeColor = errorColors()[1];
  return {
    [ChangeColor.TEXT]: token.colorText,
    [ChangeColor.BAD]: negativeColor,
    [ChangeColor.GOOD]: positiveColor,
    [ChangeColor.SWAP1]: label.startsWith("-") ? negativeColor : positiveColor,
    [ChangeColor.SWAP2]: label.startsWith("-") ? positiveColor : negativeColor,
  }[color];
}

export function getChangeArrowAngle(value: string | number) {
  const numb = Number.parseFloat(`${value}`);
  return numb === 0 ? 0 : numb < 0 ? 45 : -45;
}
